import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle, Info } from "lucide-react";
import { CustomSelect } from "../CustomSelection";
import RentalDataUseModal from "./RentalDataUseModal";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";

const EMPLOYMENT_TYPE_OPTS = [
  { value: "", label: "Employment type" },
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "casual", label: "Casual" },
  { value: "contract", label: "Contract" },
  { value: "self-employed", label: "Self-employed" },
  { value: "unemployed", label: "Unemployed" },
  { value: "student", label: "Student" },
  { value: "retired", label: "Retired" },
  { value: "other", label: "Other" },
];

const START_MONTH_OPTS = [
  { value: "", label: "Month" },
  { value: "1", label: "01-Jan" },
  { value: "2", label: "02-Feb" },
  { value: "3", label: "03-Mar" },
  { value: "4", label: "04-Apr" },
  { value: "5", label: "05-May" },
  { value: "6", label: "06-Jun" },
  { value: "7", label: "07-Jul" },
  { value: "8", label: "08-Aug" },
  { value: "9", label: "09-Sep" },
  { value: "10", label: "10-Oct" },
  { value: "11", label: "11-Nov" },
  { value: "12", label: "12-Dec" },
];

const buildYearOpts = () => {
  const y = new Date().getFullYear();
  const out = [{ value: "", label: "Year" }];
  for (let yr = y; yr >= y - 60; yr -= 1) {
    out.push({ value: String(yr), label: String(yr) });
  }
  return out;
};

const REFERENCE_TYPE_OPTS = [
  { value: "", label: "Reference type" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR representative" },
  { value: "colleague", label: "Colleague" },
  { value: "business-owner", label: "Business owner" },
  { value: "other", label: "Other" },
];

const inputClass =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#1A1A1A] outline-none transition-shadow placeholder:text-[#9CA3AF] focus:border-[#097FE6] focus:ring-2 focus:ring-[#097FE6]/20";

const Employment = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection } = useRentalProfile();
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [employmentType, setEmploymentType] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [startYear, setStartYear] = useState("");
  const [refType, setRefType] = useState("");
  const [refFullName, setRefFullName] = useState("");
  const [refEmail, setRefEmail] = useState("");
  const [refPhone, setRefPhone] = useState("");

  const yearOpts = useMemo(() => buildYearOpts(), []);

  useEffect(() => {
    if (loading) return;
    const e = profile.employment;
    setEmploymentType(e.employmentType || "");
    setCompanyName(e.companyName || "");
    setRoleTitle(e.roleTitle || "");
    setStartMonth(e.startDate?.month || "");
    setStartYear(e.startDate?.year || "");
    setRefType(e.reference?.type || "");
    setRefFullName(e.reference?.fullName || "");
    setRefEmail(e.reference?.email || "");
    setRefPhone(e.reference?.phone || "");
  }, [loading, profile.employment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.EMPLOYMENT, {
      employmentType,
      companyName: companyName.trim(),
      roleTitle: roleTitle.trim(),
      startDate: { month: startMonth, year: startYear },
      reference: {
        type: refType,
        fullName: refFullName.trim(),
        email: refEmail.trim(),
        phone: refPhone.trim(),
      },
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Employment
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <section className="space-y-5">
              <h2 className="text-base font-semibold text-[#000929]">Employment</h2>

              <div>
                <label
                  htmlFor="emp-type"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Employment type
                </label>
                <CustomSelect
                  id="emp-type"
                  value={employmentType}
                  onChange={setEmploymentType}
                  options={EMPLOYMENT_TYPE_OPTS}
                  placeholder="Employment type"
                  usePortal={false}
                />
              </div>

              <div>
                <label
                  htmlFor="emp-company"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Company name
                </label>
                <input
                  id="emp-company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={inputClass}
                  autoComplete="organization"
                />
              </div>

              <div>
                <label
                  htmlFor="emp-role"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Role or title
                </label>
                <input
                  id="emp-role"
                  type="text"
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  className={inputClass}
                  autoComplete="organization-title"
                />
              </div>

              <div>
                <span className="mb-1.5 block text-sm font-medium text-[#374151]">
                  Start date
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <CustomSelect
                    id="emp-start-month"
                    value={startMonth}
                    onChange={setStartMonth}
                    options={START_MONTH_OPTS}
                    placeholder="Month"
                    usePortal={false}
                  />
                  <CustomSelect
                    id="emp-start-year"
                    value={startYear}
                    onChange={setStartYear}
                    options={yearOpts}
                    placeholder="Year"
                    usePortal={false}
                  />
                </div>
              </div>
            </section>

            <div className="rounded-xl bg-[#F3F4F6] p-5 md:p-6 space-y-5">
              <h3 className="text-base font-semibold text-[#000929]">
                Employment reference
              </h3>
              <p className="text-sm leading-relaxed text-[#6B7280]">
                Your referee must agree to us contacting them about your
                employment. Only provide details for someone who has given you
                consent.
              </p>

              <div className="flex gap-3 rounded-xl bg-[#EBF5FF] px-4 py-3.5">
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-[#097FE6]"
                  strokeWidth={2}
                />
                <p className="text-sm leading-relaxed text-[#1A1A1A]">
                  Your reference will receive an email and SMS asking them to
                  confirm your employment details.
                </p>
              </div>

              <div>
                <label
                  htmlFor="emp-ref-type"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Reference type
                </label>
                <CustomSelect
                  id="emp-ref-type"
                  value={refType}
                  onChange={setRefType}
                  options={REFERENCE_TYPE_OPTS}
                  placeholder="Reference type"
                  usePortal={false}
                />
              </div>

              <div>
                <label
                  htmlFor="emp-ref-name"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Full name
                </label>
                <input
                  id="emp-ref-name"
                  type="text"
                  value={refFullName}
                  onChange={(e) => setRefFullName(e.target.value)}
                  className={inputClass}
                  placeholder="First and last name"
                  autoComplete="name"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center gap-2">
                  <label
                    htmlFor="emp-ref-email"
                    className="text-sm font-medium text-[#374151]"
                  >
                    Email
                  </label>
                  <span
                    className="inline-flex text-[#6B7280]"
                    title="We use this only to contact your referee about your employment."
                  >
                    <Info className="h-4 w-4" strokeWidth={2} />
                  </span>
                </div>
                <input
                  id="emp-ref-email"
                  type="email"
                  value={refEmail}
                  onChange={(e) => setRefEmail(e.target.value)}
                  className={inputClass}
                  autoComplete="email"
                />
              </div>

              <div>
                <label
                  htmlFor="emp-ref-phone"
                  className="mb-1.5 block text-sm font-medium text-[#374151]"
                >
                  Phone number
                </label>
                <input
                  id="emp-ref-phone"
                  type="tel"
                  value={refPhone}
                  onChange={(e) => setRefPhone(e.target.value)}
                  className={inputClass}
                  placeholder="Mobile or Landline"
                  autoComplete="tel"
                />
              </div>

              <p className="text-xs leading-relaxed text-[#6B7280]">
                To confirm your employment, we&apos;ll send your referee a SMS
                &amp; email.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-medium text-[#097FE6] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="employment"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default Employment;
